import { CircleAlert, CircleCheck, FileText, Loader2, X } from 'lucide-react';

import { Progress } from '@/components/ui/progress';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

import type { fileProgressProps } from '@/components/ui/bulk-file-import';

function FileNameLabel({ fileName }: { fileName: string }) {
  const parts = fileName.split('.');
  const extension = parts.pop();
  const base = parts.join('.');
  return (
    <span className="flex min-w-0 items-baseline">
      <span className="truncate">
        <Typography variant="small">{base}</Typography>
      </span>
      <span className="shrink-0">
        <Typography variant="small-semibold">.{extension}</Typography>
      </span>
    </span>
  );
}

/**
 * Per-file upload progress row (icon, name, bar, status text, cancel) used by bulk
 * import flows. Renders `status.file.name` only — file size is not displayed.
 *
 * @category Feedback
 */
export const FileProgress = ({
  status,
  onCancel,
  inProgress,
}: fileProgressProps) => {
  const isComplete = status.progress === 100 || status.isError;
  const showSpinner = inProgress && !status.isError;
  const infoText = status.isError
    ? status.errorMessage
    : inProgress
      ? 'Uploading in progress...'
      : 'File uploaded';

  return (
    <section className="mb-4 w-full">
      <div className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center text-muted-foreground">
          {showSpinner ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          ) : status.isError ? (
            <CircleAlert className="h-5 w-5 text-destructive" aria-hidden="true" />
          ) : (
            <FileText className="h-5 w-5" aria-hidden="true" />
          )}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <FileNameLabel fileName={status.file.name} />
            {isComplete &&
              (status.isError ? (
                <CircleAlert className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              ) : (
                <CircleCheck className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              ))}
          </div>
          <Progress
            value={status.isError ? 100 : status.progress}
            className={cn(
              'h-1 rounded-sm',
              status.isError
                ? '[&>[data-state]]:bg-destructive'
                : '[&>[data-state]]:bg-success',
            )}
          />
          <Typography
            variant="small"
            className={cn(status.isError ? 'text-destructive' : 'text-muted-foreground')}
          >
            {infoText}
          </Typography>
        </div>

        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel Upload"
          className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};
